/**
 * Vim keybindings for the editor — off by default, an explicit opt-in for
 * the "lives in a terminal" persona SPEC.md §1 names. Mirrors
 * `interfaceTheme.ts`'s shape exactly: a persisted boolean (rule 9's
 * try/catch'd storage), plus a minimal subscribe/notify store so
 * `VimModeToggle.tsx`'s button and the command palette's equivalent entry
 * stay in sync no matter which one triggers the change.
 */
import { readStorage, writeStorage } from './storage';

export const VIM_MODE_STORAGE_KEY = 'plotter:vim-mode';

function readPersistedVimMode(): boolean {
  return readStorage(VIM_MODE_STORAGE_KEY) === '1';
}

function persistVimMode(enabled: boolean): void {
  writeStorage(VIM_MODE_STORAGE_KEY, enabled ? '1' : '0');
}

let currentVimMode: boolean | undefined;
const listeners = new Set<(enabled: boolean) => void>();

function ensureHydrated(): boolean {
  if (currentVimMode === undefined) currentVimMode = readPersistedVimMode();
  return currentVimMode;
}

/** The current setting — read this, not `readPersistedVimMode` directly, from any UI that needs to stay in sync with a change made elsewhere. */
export function getVimMode(): boolean {
  return ensureHydrated();
}

export function subscribeVimMode(listener: (enabled: boolean) => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** The one toggle action — both `VimModeToggle.tsx`'s button and the command palette's entry call this, never a parallel implementation. */
export function toggleVimMode(): void {
  const next = !ensureHydrated();
  currentVimMode = next;
  persistVimMode(next);
  for (const listener of listeners) listener(next);
}
